import { UserFriendlyError } from '@affine/error';
import { gqlFetcherFactory } from '@affine/graphql';

import { DummyConnection } from '../../connection';
import {
  CloudBackendDegradationError,
  readBackendDegradationFromBody,
  readErrorCodeFromBody,
} from './backend-degradation-error';

export class HttpConnection extends DummyConnection {
  readonly fetch = async (
    input: string,
    init?: RequestInit & { timeout?: number }
  ) => {
    const externalSignal = init?.signal;
    if (externalSignal?.aborted) {
      throw externalSignal.reason;
    }

    const abortController = new AbortController();
    externalSignal?.addEventListener('abort', reason => {
      abortController.abort(reason);
    });

    const timeout = init?.timeout ?? 15000;
    const timeoutId = setTimeout(() => {
      abortController.abort(new Error('request timeout'));
    }, timeout);

    const res = await globalThis
      .fetch(new URL(input, this.serverBaseUrl), {
        ...init,
        signal: abortController.signal,
        credentials: 'include',
        headers: {
          ...this.requestHeaders,
          ...init?.headers,
          'x-streamlain-version': BUILD_CONFIG.appVersion,
        },
      })
      .catch(() => {
        throw new CloudBackendDegradationError(
          504,
          'NETWORK_ERROR',
          'Der Arbeitsbereich ist gerade nicht vollständig verfügbar.'
        );
      });
    clearTimeout(timeoutId);
    if (!res.ok && res.status !== 404) {
      if (res.status === 413) {
        throw new UserFriendlyError({
          status: 413,
          code: 'CONTENT_TOO_LARGE',
          type: 'CONTENT_TOO_LARGE',
          name: 'CONTENT_TOO_LARGE',
          message: 'Content too large',
        });
      } else if (
        res.headers.get('Content-Type')?.startsWith('application/json')
      ) {
        const body = await res.json();
        const degradation = readBackendDegradationFromBody(body);
        if (degradation) {
          throw new CloudBackendDegradationError(
            res.status,
            degradation.code,
            'Der Arbeitsbereich ist gerade nicht vollständig verfügbar.',
            degradation
          );
        }
        const code = readErrorCodeFromBody(body);
        if (code?.startsWith('DATABASE_SHARD_') || code === 'NETWORK_ERROR') {
          throw new CloudBackendDegradationError(
            res.status,
            code,
            'Der Arbeitsbereich ist gerade nicht vollständig verfügbar.'
          );
        }
        throw UserFriendlyError.fromAny(body);
      } else {
        throw UserFriendlyError.fromAny(await res.text());
      }
    }
    return res;
  };

  readonly fetchArrayBuffer = async (input: string, init?: RequestInit) => {
    const res = await this.fetch(input, init);
    if (res.status === 404) {
      // 404
      return null;
    }
    try {
      return await res.arrayBuffer();
    } catch (err) {
      throw new Error('fetch download error: ' + err);
    }
  };

  readonly gql = gqlFetcherFactory(
    new URL('/graphql', this.serverBaseUrl).href,
    this.fetch
  );

  constructor(
    private readonly serverBaseUrl: string,
    private readonly requestHeaders?: Record<string, string>
  ) {
    super();
  }
}
